import React, { useEffect, useState } from 'react'
import CKEditorContent from '../CKEditorContent/CKEditorContent'
import Paginator from '../Paginator/Paginator'
import CardModal from '../CardModal/CardModal'
import styled from 'styled-components'
import { MainContainer } from '../../theme/mainContainer.styled'
import pxToRem from '../../utils/pxToRem'
import { deviceMax } from '../../theme/breakpoints'
import LeadershipMatcher from '../../interfaces/LeadershipMatcher'
import NormalizeMedia from '../../utils/NormalizeMedia'
import useMediaQuery from '../../hooks/useMediaQuery'

const PAGE = 'page'
const ITEMS_PER_PAGE = 12
const ESCAPE_KEY = 'Escape'
const KEYDOWN = 'keydown'

const StyledBody = styled(MainContainer)`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
`

const StyledCardList = styled.div`
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  display: flex;
  margin: ${pxToRem(10)} auto 0 auto;
  @media ${deviceMax.tablet} {
    flex-direction: column;
    margin: ${pxToRem(10)} 0 0 0;
    width: 100%;
  }
`

const StyledCard = styled.div`
  margin: ${pxToRem(20)} ${({ $right }) => ($right ? '0' : pxToRem(10))}
    ${pxToRem(20)} ${({ $left }) => ($left ? '0' : pxToRem(10))};
  padding: ${pxToRem(10)};
  height: auto;
  border-radius: ${pxToRem(5)};
  background-color: ${({ theme }) => theme.colors.unique.white};
  cursor: ${({ $canClick }) => ($canClick ? 'pointer' : 'default')};
  @media ${deviceMax.laptopL} {
    margin: ${pxToRem(20)} ${pxToRem(10)} ${pxToRem(20)} ${pxToRem(10)};
  }
  @media ${deviceMax.tablet} {
    flex-direction: row;
    display: flex;
    width: calc(100% - ${pxToRem(20)});
    margin: ${pxToRem(5)} ${pxToRem(10)};
    height: auto;
  }
`

const StyledTitle = styled.div`
  overflow-wrap: normal;
  max-width: ${pxToRem(264)};
  font-weight: ${({ theme }) => theme.fonts.weight.semibold};
  font-size: ${({ theme }) => theme.fonts.sizes.titleH3};
  line-height: ${({ theme }) => theme.fonts.lineHeight.titleH3};
  margin: ${pxToRem(10)} 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const StyledSubtitle = styled.div`
  flex-direction: row;
  flex-wrap: wrap;
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fonts.sizes.button};
  line-height: ${({ theme }) => theme.fonts.lineHeight.button};
  color: ${({ theme }) => theme.colors.opacity.black70};
`

const StyledHorizontalSlash = styled.div`
  width: ${pxToRem(20)};
  height: ${pxToRem(2)};
  background-color: ${({ theme }) => theme.colors.primary.lime};
  margin-right: ${pxToRem(10)};
`

const StyledMedia = styled.div`
  border-radius: ${pxToRem(30)};
  height: ${pxToRem(264)};
  width: ${pxToRem(264)};

  img,
  video,
  iframe {
    height: ${pxToRem(264)};
    width: 100%;
    object-fit: cover;
    border-radius: ${pxToRem(5)} ${pxToRem(5)} ${pxToRem(30)} ${pxToRem(5)};
  }

  @media ${deviceMax.tablet} {
    height: ${pxToRem(80)};
    width: ${pxToRem(80)};
    margin-right: ${pxToRem(10)};
    img,
    video,
    iframe {
      height: ${pxToRem(80)};
    }
  }
`

const StyledSubtitleText = styled.div`
  max-width: ${pxToRem(234)};
  height: 100%;
  overflow: hidden;

  @media ${deviceMax.tablet} {
    max-width: none;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`

const StyledMobileDescription = styled.div`
  flex-direction: column;
  display: flex;
  width: calc(100% - ${pxToRem(80)});
`
const StyledCKEditorContent = styled(CKEditorContent)`
  @media ${deviceMax.tablet} {
    margin: 0 ${pxToRem(20)};
  }
`

const isBrowser = typeof window !== 'undefined'

const LeadershipTab = ({
  activeTab,
  location,
  type = LeadershipMatcher.People,
  handleActiveTab,
}) => {
  const [tappedCard, setTappedCard] = useState(false)
  const [currentList, setCurrentList] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [show, setShow] = useState(false)
  const [count, setCount] = useState(0)

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    setCurrentPage(parseInt(urlParams.get(PAGE), 10) || 1)
    setCurrentList(
      activeTab?.contents?.slice(
        ((parseInt(urlParams.get(PAGE), 10) || 1) - 1) * ITEMS_PER_PAGE,
        (parseInt(urlParams.get(PAGE), 10) || 1) * ITEMS_PER_PAGE
      )
    )
    setCount(Math.ceil(activeTab?.contents?.length / ITEMS_PER_PAGE))
  }, [currentPage, location.search])

  useEffect(() => {
    setCurrentList(
      activeTab?.contents?.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      )
    )
    setCount(Math.ceil(activeTab?.contents?.length / ITEMS_PER_PAGE))
  }, [activeTab])

  useEffect(() => {
    if (!tappedCard) return
    if (type === LeadershipMatcher.Media) {
      if (isBrowser) {
        return tappedCard.url && window.open(tappedCard.url, '_blank')
      }
      return
    }
    return setShow(true)
  }, [type, tappedCard])

  useEffect(() => {
    window.addEventListener(KEYDOWN, (e) => closeOverlay(e))
    return window.removeEventListener(KEYDOWN, (e) => closeOverlay(e))
  }, [])

  const removeModal = () => {
    setTappedCard(null)
    return setShow(false)
  }

  const closeOverlay = (e) => {
    if (e.key === ESCAPE_KEY) return setShow(false)
  }
  const isMobile = useMediaQuery(deviceMax.tablet)

  const list = currentList ? currentList : activeTab?.contents
  const styledCards = list?.map((card, index) => {
    const canClick =
      (type === LeadershipMatcher.Media && list[index]?.url) ||
      card?.descriptionHtml?.content
    if (isMobile) {
      return (
        <StyledCard
          key={card.id + index}
          $canClick={canClick}
          $right={index % 4 === 3}
          $left={index % 4 === 0}
          onClick={() => setTappedCard(card)}
        >
          <StyledMedia>{NormalizeMedia(card.image)}</StyledMedia>
          <StyledMobileDescription>
            <StyledTitle>{card.titleLevel1}</StyledTitle>
            {card.titleLevel2 && (
              <StyledSubtitle>
                <StyledSubtitleText>
                  <StyledHorizontalSlash />
                  {card.titleLevel2}
                </StyledSubtitleText>
              </StyledSubtitle>
            )}
            {card.titleLevel3 && (
              <StyledSubtitle>{card.titleLevel3} </StyledSubtitle>
            )}
          </StyledMobileDescription>
        </StyledCard>
      )
    }

    return (
      <StyledCard
        key={card.id + index}
        $canClick={canClick}
        $right={index % 4 === 3}
        $left={index % 4 === 0}
        onClick={() => setTappedCard(card)}
      >
        <StyledMedia>{NormalizeMedia(card.image)}</StyledMedia>
        <StyledTitle>{card.titleLevel1}</StyledTitle>
        {card.titleLevel2 && (
          <StyledSubtitle>
            <StyledHorizontalSlash />
            <StyledSubtitleText>{card.titleLevel2}</StyledSubtitleText>
          </StyledSubtitle>
        )}
        {card.titleLevel3 && (
          <StyledSubtitle>{card.titleLevel3} </StyledSubtitle>
        )}
      </StyledCard>
    )
  })

  return (
    <>
      <StyledBody>
        <StyledCKEditorContent
          html={activeTab?.textHtml ? activeTab?.textHtml.content : ''}
        />
        <StyledCardList>{styledCards}</StyledCardList>
        {count > 1 && (
          <Paginator
            count={count}
            currentPage={currentPage}
            tabName={activeTab?.tabTitle?.replaceAll(' ', '-')}
          />
        )}
      </StyledBody>
      {tappedCard?.descriptionHtml?.content && show && (
        <CardModal
          isMobile={isMobile}
          onClose={() => {
            removeModal()
          }}
          card={tappedCard}
        />
      )}
    </>
  )
}

export default LeadershipTab
